import { isEmpty, map } from "lodash";
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import Button from "antd/lib/button";
import Checkbox from "antd/lib/checkbox";
import routeWithUserSession from "@/components/ApplicationArea/routeWithUserSession";
import DynamicComponent from "@/components/DynamicComponent";
import DashboardGrid from "@/components/dashboards/DashboardGrid";
import Parameters from "@/components/Parameters";
import Filters from "@/components/Filters";

import { Dashboard } from "@/services/dashboard";
import recordEvent from "@/services/recordEvent";
import resizeObserver from "@/services/resizeObserver";
import routes from "@/services/routes";
import location from "@/services/location";
import url from "@/services/url";
import useImmutableCallback from "@/lib/hooks/useImmutableCallback";

import useDashboard from "./hooks/useDashboard";
import DashboardHeader from "./components/DashboardHeader";
import DashboardBackgroundDialog from "@/components/dashboards/DashboardBackgroundDialog";

import "./DashboardPage.less";

function DashboardBackgroundLayer({ imageUrl, opacity }) {
  // imageUrl 为空时不渲染背景图（background-image: none）
  const style = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: "none",
    backgroundSize: "100% 100%",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
    opacity: opacity !== undefined ? opacity : 1.0,
  };
  return <div className="dashboard-bg" style={style} />;
}

DashboardBackgroundLayer.propTypes = {
  imageUrl: PropTypes.string,
  opacity: PropTypes.number,
};

DashboardBackgroundLayer.defaultProps = {
  imageUrl: undefined,
  opacity: undefined,
};

function DashboardSettings({ dashboardConfiguration }) {
  const { dashboard, updateDashboard } = dashboardConfiguration;
  return (
    <div className="m-b-10 p-15 bg-white tiled">
      <Checkbox
        checked={!!dashboard.dashboard_filters_enabled}
        onChange={({ target }) => updateDashboard({ dashboard_filters_enabled: target.checked })}
        data-test="DashboardFiltersCheckbox">
        Use Dashboard Level Filters
      </Checkbox>
    </div>
  );
}

DashboardSettings.propTypes = {
  dashboardConfiguration: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

function AddWidgetContainer({ dashboardConfiguration, onAppearanceSettings, className, ...props }) {
  const { showAddTextboxDialog, showAddWidgetDialog } = dashboardConfiguration;
  return (
    <div className={cx("add-widget-container", className)} {...props}>
      <h2>
        <i className="zmdi zmdi-widgets" aria-hidden="true" />
        <span className="hidden-xs hidden-sm">
          Widgets are individual query visualizations or text boxes you can place on your dashboard in various
          arrangements.
        </span>
      </h2>
      <div>
        <Button className="m-r-15" onClick={onAppearanceSettings}>
          <i className="zmdi zmdi-palette m-r-5" aria-hidden="true" />
          Appearance
        </Button>
        <Button className="m-r-15" onClick={showAddTextboxDialog} data-test="AddTextboxButton">
          Add Textbox
        </Button>
        <Button type="primary" onClick={showAddWidgetDialog} data-test="AddWidgetButton">
          Add Widget
        </Button>
      </div>
    </div>
  );
}

AddWidgetContainer.propTypes = {
  dashboardConfiguration: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onAppearanceSettings: PropTypes.func,
  className: PropTypes.string,
};

function DashboardComponent(props) {
  const dashboardConfiguration = useDashboard(props.dashboard);
  const {
    dashboard,
    filters,
    setFilters,
    loadDashboard,
    loadWidget,
    removeWidget,
    saveDashboardLayout,
    globalParameters,
    updateDashboard,
    refreshDashboard,
    refreshWidget,
    editingLayout,
    setGridDisabled,
  } = dashboardConfiguration;

  const [pageContainer, setPageContainer] = useState(null);
  const [bottomPanelStyles, setBottomPanelStyles] = useState({});
  const [previewBackground, setPreviewBackground] = useState(null);
  const [previewGridMargin, setPreviewGridMargin] = useState(null);
  const onParametersEdit = parameters => {
    const paramOrder = map(parameters, "name");
    updateDashboard({ options: { globalParamOrder: paramOrder } });
  };

  useEffect(() => {
    if (pageContainer) {
      const unobserve = resizeObserver(pageContainer, () => {
        if (editingLayout) {
          const style = window.getComputedStyle(pageContainer, null);
          const bounds = pageContainer.getBoundingClientRect();
          const paddingLeft = parseFloat(style.paddingLeft) || 0;
          const paddingRight = parseFloat(style.paddingRight) || 0;
          setBottomPanelStyles({
            left: Math.round(bounds.left) + paddingRight,
            width: pageContainer.clientWidth - paddingLeft - paddingRight,
          });
        }

        // reflow grid when container changes its size
        window.dispatchEvent(new Event("resize"));
      });
      return unobserve;
    }
  }, [pageContainer, editingLayout]);

  const handleBgClick = useCallback(
    () => {
      if (!editingLayout) return;

      // 记录快照（用于取消时恢复）
      const snapshot = {
        background: dashboard.options && dashboard.options.background ? { ...dashboard.options.background } : null,
        gridMargin: dashboard.options ? dashboard.options.gridMargin : undefined,
      };

      DashboardBackgroundDialog.showModal({
        options: dashboard.options || {},
        onPreview: newOpts => {
          setPreviewBackground(newOpts.background);
          setPreviewGridMargin(newOpts.gridMargin);
        },
        onSave: newOpts => {
          // 清除预览状态（以持久化的 dashboard.options 为准）
          setPreviewBackground(null);
          setPreviewGridMargin(null);
          updateDashboard({
            options: {
              ...(dashboard.options || {}),
              background: newOpts.background,
              gridMargin: newOpts.gridMargin,
            },
          });
        },
      }).onDismiss(() => {
        // 取消时恢复快照，然后清除预览（让 UI 恢复到 dashboard.options 的状态）
        setPreviewBackground(snapshot.background);
        setPreviewGridMargin(snapshot.gridMargin !== undefined ? snapshot.gridMargin : null);
        // 等一个 tick 后清除预览
        setTimeout(() => {
          setPreviewBackground(null);
          setPreviewGridMargin(null);
        }, 0);
      });
    },
    [editingLayout, dashboard.options, updateDashboard]
  );

  return (
    <div className="container" ref={setPageContainer} data-test={`DashboardId${dashboard.id}Container`}>
      <DashboardHeader
        dashboardConfiguration={dashboardConfiguration}
        headerExtra={
          <DynamicComponent
            name="Dashboard.HeaderExtra"
            dashboard={dashboard}
            dashboardConfiguration={dashboardConfiguration}
          />
        }
      />
      {!isEmpty(globalParameters) && (
        <div className="dashboard-parameters m-b-10 p-15 bg-white tiled" data-test="DashboardParameters">
          <Parameters
            parameters={globalParameters}
            onValuesChange={refreshDashboard}
            sortable={editingLayout}
            onParametersEdit={onParametersEdit}
          />
        </div>
      )}
      {!isEmpty(filters) && (
        <div className="m-b-10 p-15 bg-white tiled" data-test="DashboardFilters">
          <Filters filters={filters} onChange={setFilters} />
        </div>
      )}
      {editingLayout && <DashboardSettings dashboardConfiguration={dashboardConfiguration} />}
      <div style={{ position: "relative" }} id="dashboard-container">
        <DashboardBackgroundLayer
          imageUrl={
            previewBackground !== null
              ? previewBackground && previewBackground.image_url
              : dashboard.options && dashboard.options.background
              ? dashboard.options.background.image_url
              : undefined
          }
          opacity={
            previewBackground !== null
              ? previewBackground && previewBackground.opacity
              : dashboard.options && dashboard.options.background
              ? dashboard.options.background.opacity
              : undefined
          }
        />
        <DashboardGrid
          dashboard={previewGridMargin !== null
            ? { ...dashboard, options: { ...(dashboard.options || {}), gridMargin: previewGridMargin } }
            : dashboard}
          widgets={dashboard.widgets}
          filters={filters}
          isEditing={editingLayout}
          onLayoutChange={editingLayout ? saveDashboardLayout : () => {}}
          onBreakpointChange={setGridDisabled}
          onLoadWidget={loadWidget}
          onRefreshWidget={refreshWidget}
          onRemoveWidget={removeWidget}
          onParameterMappingsChange={loadDashboard}
        />
      </div>
      {editingLayout && (
        <AddWidgetContainer dashboardConfiguration={dashboardConfiguration} onAppearanceSettings={handleBgClick} style={bottomPanelStyles} />
      )}
    </div>
  );
}

DashboardComponent.propTypes = {
  dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

function DashboardPage({ dashboardSlug, dashboardId, onError }) {
  const [dashboard, setDashboard] = useState(null);
  const handleError = useImmutableCallback(onError);

  // Bug fix #1: 普通 Dashboard 不使用 dark 主题（只有 share/public 才用）
  useEffect(() => {
    document.body.classList.remove("theme-dark");
    document.body.style.backgroundColor = "";
  }, []);

  useEffect(() => {
    Dashboard.get({ id: dashboardId, slug: dashboardSlug })
      .then(dashboardData => {
        recordEvent("view", "dashboard", dashboardData.id);
        setDashboard(dashboardData);

        // if loaded by slug, update location url to use the id
        if (!dashboardId) {
          location.setPath(url.parse(dashboardData.url).pathname, true);
        }
      })
      .catch(handleError);
  }, [dashboardId, dashboardSlug, handleError]);

  return <div className="dashboard-page">{dashboard && <DashboardComponent dashboard={dashboard} />}</div>;
}

DashboardPage.propTypes = {
  dashboardSlug: PropTypes.string,
  dashboardId: PropTypes.string,
  onError: PropTypes.func,
};

DashboardPage.defaultProps = {
  dashboardSlug: null,
  dashboardId: null,
  onError: PropTypes.func,
};

// route kept for backward compatibility
routes.register(
  "Dashboards.LegacyViewOrEdit",
  routeWithUserSession({
    path: "/dashboard/:dashboardSlug",
    render: pageProps => <DashboardPage {...pageProps} />,
  })
);

routes.register(
  "Dashboards.ViewOrEdit",
  routeWithUserSession({
    path: "/dashboards/:dashboardId([^-]+)(-.*)?",
    render: pageProps => <DashboardPage {...pageProps} />,
  })
);
